import { useEffect, useMemo, useState } from "react";
import { t } from "../../shared/trendReportShared";
import { useUserDirectory } from "../../../../../hooks/useUserDirectory";
import LanguagePicker from "./LanguagePicker";

// One modal, three ways to name an audience: whole user groups, people from
// the directory, or a raw address for someone outside the company.
//
// Groups and people both come from useUserDirectory, the one loader every
// user picker shares - the newsletter does not maintain its own copy of the
// directory. Its endpoints accept trend.subscribers.manage so this tab can
// read them; see admin_routes.get_users. It loads each time the picker opens.
//
// Two things shape the flow. Adding is a *selection*, not a click per row:
// picking twelve people used to mean twelve clicks and twelve round trips, so
// the lists are checkbox tables and the selection commits as one batch. And
// the language is asked at the moment you commit - "Add selected" turns the
// footer into the language question - rather than sitting at the top of the
// modal as a mode you have to notice before you start. The choice now belongs
// to a decision instead of to the furniture, and it appears where you clicked.
const TABS = [
  { id: "groups", labelKey: "newsletterTabGroups" },
  { id: "people", labelKey: "newsletterTabPeople" },
  { id: "email", labelKey: "newsletterTabEmail" },
];

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[A-Za-z]{2,}$/;

// The directory can run to thousands of users; rendering all of them behind a
// "select all" would also make that checkbox a lie about what it selects.
const PEOPLE_LIMIT = 60;

function Spinner({ className = "w-4 h-4" }) {
  return (
    <svg
      className={`${className} animate-spin`}
      viewBox="0 0 24 24"
      fill="none"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );
}

function Check({ className = "w-3.5 h-3.5" }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={3}
        d="M5 13l4 4L19 7"
      />
    </svg>
  );
}

function Checkbox({
  checked,
  indeterminate = false,
  disabled,
  onChange,
  label,
}) {
  return (
    <input
      type="checkbox"
      checked={checked}
      disabled={disabled}
      aria-label={label}
      onChange={onChange}
      ref={(el) => {
        if (el) el.indeterminate = indeterminate;
      }}
      className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-orange-500 focus:ring-orange-400/60 disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed"
    />
  );
}

const TH =
  "px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wide text-gray-400";
const TD = "px-3 py-2.5 align-middle";

export default function AudiencePicker({
  language,
  audiences,
  subscribers,
  onAddMany,
  onClose,
}) {
  const directory = useUserDirectory();
  const [tab, setTab] = useState("groups");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(() => new Map());
  const [armed, setArmed] = useState(false);
  const [lang, setLang] = useState("both");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  // Escape disarms the footer before it closes the modal, and does neither
  // mid-write.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== "Escape" || busy) return;
      if (armed) setArmed(false);
      else onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose, armed, busy]);

  // Reset the search when switching tabs - a group query is meaningless
  // against the people list and just shows a confusing empty state.
  useEffect(() => setQuery(""), [tab]);

  const takenGroups = useMemo(
    () => new Set((audiences.items || []).map((a) => a.group_id)),
    [audiences.items],
  );
  const takenEmails = useMemo(
    () =>
      new Set(
        (subscribers.items || []).map((s) => (s.email || "").toLowerCase()),
      ),
    [subscribers.items],
  );

  const q = query.trim().toLowerCase();

  const groups = useMemo(
    () =>
      (directory.groups || []).filter(
        (g) => !q || (g.id || "").toLowerCase().includes(q),
      ),
    [directory.groups, q],
  );

  const matchedPeople = useMemo(
    () =>
      (directory.users || [])
        .filter((u) => u.email)
        .filter(
          (u) =>
            !q ||
            u.email.toLowerCase().includes(q) ||
            (u.displayName || "").toLowerCase().includes(q),
        ),
    [directory.users, q],
  );
  const people = matchedPeople.slice(0, PEOPLE_LIMIT);
  const peopleTruncated = matchedPeople.length > PEOPLE_LIMIT;

  // ── selection ──────────────────────────────────────────────────────────────
  // Selection is one map across all three tabs, so a batch can mix groups,
  // directory people and typed-in addresses.

  // Touching the selection clears the last batch's result: it describes rows
  // that are no longer what the footer is about to act on.
  const clearResult = () => {
    setNotice("");
    setError("");
  };

  const toggleEntry = (entry) => {
    clearResult();
    setSelected((prev) => {
      const next = new Map(prev);
      if (!next.delete(entry.key)) next.set(entry.key, entry);
      return next;
    });
  };

  const setMany = (entries, on) => {
    clearResult();
    setSelected((prev) => {
      const next = new Map(prev);
      entries.forEach((e) => (on ? next.set(e.key, e) : next.delete(e.key)));
      return next;
    });
  };

  const groupEntry = (g) => ({
    key: `group:${g.id}`,
    kind: "group",
    label: g.id,
    sublabel: `${g.memberCount ?? 0} ${t("newsletterMembers", language)}`,
    groupId: g.id,
  });

  const personEntry = (u) => ({
    key: `person:${u.email.toLowerCase()}`,
    kind: "person",
    label: u.displayName || u.email,
    sublabel: u.email,
    payload: {
      email: u.email,
      display_name: u.displayName || undefined,
      firebase_uid: u.uid || u.id,
    },
  });

  const selectableGroups = groups
    .filter((g) => !takenGroups.has(g.id))
    .map(groupEntry);
  const selectablePeople = people
    .filter((u) => !takenEmails.has(u.email.toLowerCase()))
    .map(personEntry);

  const visibleSelectable =
    tab === "groups"
      ? selectableGroups
      : tab === "people"
        ? selectablePeople
        : [];
  const visibleSelectedCount = visibleSelectable.filter((e) =>
    selected.has(e.key),
  ).length;
  const allVisibleSelected =
    visibleSelectable.length > 0 &&
    visibleSelectedCount === visibleSelectable.length;

  const emailValue = email.trim();
  const emailValid = EMAIL_RE.test(emailValue);
  const emailTaken = takenEmails.has(emailValue.toLowerCase());
  const emailQueued = selected.has(`person:${emailValue.toLowerCase()}`);

  const queueTypedEmail = () => {
    toggleEntry({
      key: `person:${emailValue.toLowerCase()}`,
      kind: "person",
      label: name.trim() || emailValue,
      sublabel: emailValue,
      external: true,
      payload: {
        email: emailValue,
        display_name: name.trim() || undefined,
      },
    });
    setEmail("");
    setName("");
  };

  // ── commit ─────────────────────────────────────────────────────────────────

  const entries = [...selected.values()];

  const commit = async () => {
    setBusy(true);
    setError("");
    setNotice("");
    setProgress({ done: 0, total: entries.length });
    try {
      const { added, failed } = await onAddMany(
        entries.map((e) => ({ ...e, language: lang })),
        (done, total) => setProgress({ done, total }),
      );

      // Keep the failures selected so they can be retried without being hunted
      // down in the directory again; drop everything that landed.
      setSelected(new Map(failed.map((f) => [f.key, selected.get(f.key)])));
      setArmed(false);

      // Both halves of a partial batch are worth saying, so they are reported
      // side by side rather than one overwriting the other.
      setError(
        failed.length > 0
          ? `${failed.length} ${t("newsletterAddFailedCount", language)} — ${failed[0].error}`
          : "",
      );
      setNotice(
        added > 0 ? `${added} ${t("newsletterAddedResult", language)}` : "",
      );
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  // ── render ─────────────────────────────────────────────────────────────────

  const statusCell = (taken) =>
    taken ? (
      <span className="text-[11px] text-gray-400">
        {t("newsletterAlreadyAdded", language)}
      </span>
    ) : null;

  // Arming survives only while there is something to add - emptying the
  // selection puts the footer back to browsing rather than leaving a language
  // question about nothing.
  const showArmed = armed && entries.length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={() => !busy && onClose()}
        aria-hidden
      />
      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[88vh]">
        <div className="flex items-start justify-between gap-3 px-6 py-5 border-b border-gray-100 dark:border-gray-700/60">
          <div className="min-w-0">
            <h3 className="text-base font-bold text-gray-900 dark:text-white">
              {t("newsletterAddAudience", language)}
            </h3>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {t("newsletterPickerHint", language)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            aria-label={t("newsletterClose", language)}
            className="shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-1 disabled:opacity-40"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="px-6 pt-5">
          <div className="inline-flex w-full gap-1 p-1 rounded-xl bg-gray-100 dark:bg-gray-900/50">
            {TABS.map((x) => (
              <button
                key={x.id}
                type="button"
                onClick={() => setTab(x.id)}
                className={`flex-1 px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${
                  tab === x.id
                    ? "bg-orange-500 text-white"
                    : "text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-800"
                }`}
              >
                {t(x.labelKey, language)}
              </button>
            ))}
          </div>
        </div>

        <div className="px-6 py-5 overflow-y-auto flex-1 min-h-[300px]">
          {tab !== "email" && (
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t(
                tab === "groups"
                  ? "newsletterSearchGroups"
                  : "newsletterSearchUsers",
                language,
              )}
              className="w-full mb-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/40 px-4 py-2.5 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-orange-400/60"
            />
          )}

          {directory.status === "loading" && tab !== "email" && (
            <p className="text-xs text-gray-400">
              {t("newsletterLoading", language)}
            </p>
          )}
          {directory.status === "error" && tab !== "email" && (
            <p className="text-xs text-red-500">
              {t("newsletterPickUsersDenied", language)}
            </p>
          )}

          {tab === "groups" && directory.status === "ok" && (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-gray-100 dark:border-gray-700/60">
                    <th className={`${TH} w-10`}>
                      <Checkbox
                        checked={allVisibleSelected}
                        indeterminate={
                          visibleSelectedCount > 0 && !allVisibleSelected
                        }
                        disabled={selectableGroups.length === 0}
                        label={t("newsletterSelectAll", language)}
                        onChange={(e) =>
                          setMany(selectableGroups, e.target.checked)
                        }
                      />
                    </th>
                    <th className={TH}>{t("newsletterTabGroups", language)}</th>
                    <th className={TH}>
                      {t("newsletterColMembers", language)}
                    </th>
                    <th className={`${TH} text-right`}>
                      {t("newsletterColStatus", language)}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {groups.map((g) => {
                    const entry = groupEntry(g);
                    const taken = takenGroups.has(g.id);
                    const checked = selected.has(entry.key);
                    return (
                      <tr
                        key={g.id}
                        onClick={() => !taken && toggleEntry(entry)}
                        className={`border-b border-gray-50 dark:border-gray-700/40 ${
                          taken
                            ? "opacity-60"
                            : "cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900/40"
                        } ${checked ? "bg-orange-50/60 dark:bg-orange-500/5" : ""}`}
                      >
                        <td className={TD} onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={checked}
                            disabled={taken}
                            label={`${t("newsletterSelectRow", language)} ${g.id}`}
                            onChange={() => toggleEntry(entry)}
                          />
                        </td>
                        <td
                          className={`${TD} text-sm font-semibold text-gray-800 dark:text-gray-100`}
                        >
                          {g.id}
                        </td>
                        <td className={`${TD} text-[11px] text-gray-400`}>
                          {g.memberCount ?? 0}
                        </td>
                        <td className={`${TD} text-right`}>
                          {statusCell(taken)}
                        </td>
                      </tr>
                    );
                  })}
                  {groups.length === 0 && (
                    <tr>
                      <td
                        colSpan={4}
                        className="px-3 py-4 text-xs text-gray-400"
                      >
                        {t("newsletterNoGroups", language)}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {tab === "people" && directory.status === "ok" && (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-gray-100 dark:border-gray-700/60">
                    <th className={`${TH} w-10`}>
                      <Checkbox
                        checked={allVisibleSelected}
                        indeterminate={
                          visibleSelectedCount > 0 && !allVisibleSelected
                        }
                        disabled={selectablePeople.length === 0}
                        label={t("newsletterSelectAll", language)}
                        onChange={(e) =>
                          setMany(selectablePeople, e.target.checked)
                        }
                      />
                    </th>
                    <th className={TH}>{t("newsletterColName", language)}</th>
                    <th className={TH}>{t("newsletterColEmail", language)}</th>
                    <th className={TH}>{t("newsletterColGroup", language)}</th>
                    <th className={`${TH} text-right`}>
                      {t("newsletterColStatus", language)}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {people.map((u) => {
                    const entry = personEntry(u);
                    const taken = takenEmails.has(u.email.toLowerCase());
                    const checked = selected.has(entry.key);
                    return (
                      <tr
                        key={u.uid || u.id || u.email}
                        onClick={() => !taken && toggleEntry(entry)}
                        className={`border-b border-gray-50 dark:border-gray-700/40 ${
                          taken
                            ? "opacity-60"
                            : "cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900/40"
                        } ${checked ? "bg-orange-50/60 dark:bg-orange-500/5" : ""}`}
                      >
                        <td className={TD} onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={checked}
                            disabled={taken}
                            label={`${t("newsletterSelectRow", language)} ${u.email}`}
                            onChange={() => toggleEntry(entry)}
                          />
                        </td>
                        <td
                          className={`${TD} text-sm font-semibold text-gray-800 dark:text-gray-100 max-w-[12rem] truncate`}
                        >
                          {u.displayName || u.email}
                        </td>
                        <td
                          className={`${TD} text-[11px] text-gray-500 dark:text-gray-400 max-w-[14rem] truncate`}
                        >
                          {u.email}
                        </td>
                        <td className={`${TD} text-[11px] text-gray-400`}>
                          {u.groupId || "—"}
                        </td>
                        <td className={`${TD} text-right`}>
                          {statusCell(taken)}
                        </td>
                      </tr>
                    );
                  })}
                  {people.length === 0 && (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-3 py-4 text-xs text-gray-400"
                      >
                        {t("newsletterNoUsers", language)}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              {peopleTruncated && (
                <p className="mt-3 text-[11px] text-gray-400">
                  {t("newsletterTruncated", language)}
                </p>
              )}
            </div>
          )}

          {tab === "email" && (
            <div className="space-y-3 flex flex-col items-center">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("newsletterAddEmail", language)}
                className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/40 px-4 py-2.5 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-orange-400/60"
              />
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t("newsletterAddName", language)}
                className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/40 px-4 py-2.5 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-orange-400/60"
              />
              {(emailTaken || emailQueued) && (
                <p className="text-[11px] text-gray-400">
                  {t("newsletterAlreadyAdded", language)}
                </p>
              )}
              <button
                type="button"
                disabled={!emailValid || emailTaken || emailQueued}
                onClick={queueTypedEmail}
                className={`w-full py-2.5 px-4 rounded-xl font-medium text-sm transition-colors ${
                  emailValid && !emailTaken && !emailQueued
                    ? "bg-orange-500 text-white hover:bg-orange-600"
                    : "bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                }`}
              >
                + {t("newsletterAddToSelection", language)}
              </button>
            </div>
          )}
        </div>

        {/* The footer is the whole second step. Pressing "Add selected" turns
            this bar into the language question instead of swapping the modal
            out for a confirm screen: the ticked rows stay on screen behind it,
            and the choice appears under the cursor that just asked for it. */}
        <div
          className={`px-6 py-4 border-t flex flex-wrap items-center justify-between gap-3 transition-colors ${
            showArmed
              ? "border-orange-500/40 bg-orange-50/70 dark:bg-orange-500/5"
              : "border-gray-100 dark:border-gray-700/60"
          }`}
        >
          {showArmed ? (
            <div className="min-w-0 flex flex-wrap items-center gap-x-4 gap-y-2">
              <div className="min-w-0">
                <p className="text-xs font-bold text-gray-800 dark:text-gray-100">
                  {t("newsletterLanguageTitle", language)}
                </p>
                <p className="text-[11px] text-gray-500 dark:text-gray-400">
                  {t("newsletterLanguageHintShort", language)}
                </p>
              </div>
              <LanguagePicker
                value={lang}
                onChange={setLang}
                language={language}
                disabled={busy}
              />
            </div>
          ) : (
            <div className="min-w-0 text-xs flex flex-col gap-1">
              {notice && (
                <span className="inline-flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400">
                  <Check />
                  {notice}
                </span>
              )}
              {error && <span className="text-red-500">{error}</span>}
              {!notice && !error && entries.length > 0 ? (
                <span className="inline-flex items-center gap-2 text-gray-500 dark:text-gray-400">
                  <span className="font-semibold text-gray-700 dark:text-gray-200">
                    {entries.length} {t("newsletterSelectedCount", language)}
                  </span>
                  <button
                    type="button"
                    onClick={() => setSelected(new Map())}
                    className="text-[11px] font-semibold text-gray-400 hover:text-red-500"
                  >
                    {t("newsletterClearSelection", language)}
                  </button>
                </span>
              ) : (
                !notice &&
                !error && (
                  <span className="text-gray-400">
                    {t("newsletterNothingSelected", language)}
                  </span>
                )
              )}
            </div>
          )}

          <div className="shrink-0 flex items-center gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => (showArmed ? setArmed(false) : onClose())}
              className="rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/40 disabled:opacity-40"
            >
              {showArmed
                ? t("newsletterCancel", language)
                : t("newsletterClose", language)}
            </button>
            <button
              type="button"
              disabled={entries.length === 0 || busy}
              onClick={() => (showArmed ? commit() : setArmed(true))}
              className={`inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition-colors ${
                entries.length > 0 && !busy
                  ? "bg-orange-500 text-white hover:bg-orange-600"
                  : "bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed"
              }`}
            >
              {busy && <Spinner />}
              {busy && progress
                ? `${t("newsletterAdding", language)} ${progress.done}/${progress.total}`
                : `${
                    showArmed
                      ? t("newsletterAdd", language)
                      : t("newsletterAddSelected", language)
                  }${entries.length > 0 ? ` (${entries.length})` : ""}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
